import I18n from 'i18n-js';
import React from 'react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import fastTruckNudgeIcon from '@reverbdotcom/cadence/images/nudge/fast-truck.svg';
import { formatAmountCents } from '@reverbdotcom/commons/src/money';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useEppoExpEnabled, useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import {
  USPS_CARRIER_CALC_MAX_LENGTH_INCHES, USPS_CARRIER_CALC_MAX_WEIGHT_POUNDS,
} from './CarrierCalculatedUspsPackageValidator';
import URLHelpers from '../../../../shared/url_helpers';
import { ShippingMode } from './DomesticShippingCard';
import { UPS_HEAVY_PACKAGE_STICKER_REQUIRED, UPS_LONGEST_SIDE_INCHES } from './CarrierCalculatedUpsPackageValidator';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { RCTag } from '@reverbdotcom/cadence/components';
import { CarrierHighlights } from './CarrierHighlights';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { EppoSubjectTypes } from '@reverbdotcom/commons/src/eppo/eppoExperimentHelpers';

interface IProps {
  shippingMode: ShippingMode;
  shopAddressSummary?: string;
  localizedRegionName: string;
  currency: string;
}

const LOCALIZED_PRICE_EXAMPLE_CENTS = 2500;

export function ShippingRadioSublabel({ shippingMode, currentMode, shopAddressSummary }: {
  shippingMode: ShippingMode;
  currentMode: ShippingMode;
  shopAddressSummary?: string;
}) {
  const user = useUser();
  const upsEnabled = useExpEnabled(experiments.UPS_CALCULATED_SHIPPING);
  const shippingDefaultsEnabled = useEppoExpEnabled({
    flagKey: eppoFlags.SELL_FORM_SHIPPING_DEFAULTS_UI,
    subjectKey: user.id,
    subjectType: EppoSubjectTypes.USER_ID,
  });

  const shippingModeTranslationKey = (shippingMode) => {
    if (shippingMode == ShippingMode.CARRIER_CALCULATED) {
      if (shippingDefaultsEnabled) return 'carrierCalculatedShippingDefaults';
      if (upsEnabled) return 'carrierCalculatedUps';
    }

    return shippingMode;
  };


  if (shippingDefaultsEnabled && (shippingMode === ShippingMode.FREE || shippingMode === ShippingMode.CUSTOM) && currentMode !== shippingMode) {
    return null;
  }

  return (
    <>
      <div className="shipping-radio-label__sublabel">
        <span>
          <SanitizedRender
            html={I18n.t(`discovery.sellForm.shippingSection.domesticShippingCard.radios.subLabel.${shippingModeTranslationKey(shippingMode)}`, { shopAddressSummary })}
          />
        </span>

        {shippingMode === ShippingMode.CARRIER_CALCULATED && (
          <span className="shipping-radio-label__sublabel__link">
            <CoreLink
              to={URLHelpers.legalPagePath('calculated-shipping-terms-and-conditions')}
              target="_blank"
            >
              {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.radios.carrierCalcTerms')}
            </CoreLink>
          </span>
        )}
      </div>
      {
        shippingDefaultsEnabled ?
          currentMode !== ShippingMode.CARRIER_CALCULATED &&
          shippingMode === ShippingMode.CARRIER_CALCULATED &&
          upsEnabled &&
          <CarrierHighlights />
          :
          shippingMode === ShippingMode.CARRIER_CALCULATED &&
          upsEnabled &&
          <CarrierHighlights />
      }
    </>
  );
}

// TODO: Remove this once SELL_FORM_SHIPPING_DEFAULTS experiment is fully rolled out
export function ShippingRadioHelp({ shippingMode, localizedRegionName, currency }: IProps) {
  const upsEnabled = useExpEnabled(experiments.UPS_CALCULATED_SHIPPING);
  const poundsMax = upsEnabled ? UPS_HEAVY_PACKAGE_STICKER_REQUIRED : USPS_CARRIER_CALC_MAX_WEIGHT_POUNDS;
  const inchesMax = upsEnabled ? UPS_LONGEST_SIDE_INCHES : USPS_CARRIER_CALC_MAX_LENGTH_INCHES;
  const shippingModeTranslationKey = (shippingMode) => {
    if (shippingMode == ShippingMode.CARRIER_CALCULATED && upsEnabled) {
      return 'carrierCalculatedUps';
    }

    if (shippingMode == ShippingMode.CUSTOM && upsEnabled) {
      return 'customUps';
    }

    return shippingMode;
  };

  return (
    <div className="shipping-radio-label__help">
      <div className="shipping-radio-label__help__background">
        <div className="shipping-radio-label__help__box">
          <div className="shipping-radio-label__help__box__title">
            {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.radios.bestFor')}
          </div>

          <div className="text-utility-350 text-utility--semibold">
            {I18n.t(`discovery.sellForm.shippingSection.domesticShippingCard.radios.bestForType.${shippingModeTranslationKey(shippingMode)}`)}
          </div>
          {
            shippingMode === ShippingMode.CARRIER_CALCULATED && (
              <div className="shipping-radio-label__explainer__text">
                {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.radios.explainer.carrierCalculated', {
                  poundsMax,
                  inchesMax,
                })}
              </div>
            )
          }
        </div>
        <hr />
        <div className="shipping-radio-label__help__box">
          <div className="shipping-radio-label__help__box__title">
            {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.radios.buyerSees')}
          </div>
          <div className="shipping-radio-label__example__text">
            {shippingMode === ShippingMode.FREE && (
              <img src={fastTruckNudgeIcon} className="shipping-radio-label__example__icon" alt="" />
            )}
            <div className="text-utility-350 text-utility--semibold">
              {I18n.t(
                `discovery.sellForm.shippingSection.domesticShippingCard.radios.example.${shippingMode}`,
                {
                  localizedRegionName,
                  localizedPriceExample: formatAmountCents({ amountCents: LOCALIZED_PRICE_EXAMPLE_CENTS, currency }, { precision: 0 }) })
              }
            </div>
          </div>

          {shippingMode !== ShippingMode.FREE && (
            <div className="shipping-radio-label__explainer__text">
              {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.radios.disclaimer.example')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ShippingRadioLabel({ shippingMode }: { shippingMode: ShippingMode; }) {
  const user = useUser();
  const upsEnabled = useExpEnabled(experiments.UPS_CALCULATED_SHIPPING);
  const shippingDefaultsEnabled = useEppoExpEnabled({
    flagKey: eppoFlags.SELL_FORM_SHIPPING_DEFAULTS_UI,
    subjectKey: user.id,
    subjectType: EppoSubjectTypes.USER_ID,
  });


  const shippingModeTranslationKey = (mode) => {
    if (mode == ShippingMode.CARRIER_CALCULATED && (upsEnabled || shippingDefaultsEnabled)) {
      return 'carrierCalculatedUps';
    }

    return shippingMode;
  };

  return (
    <span className="shipping-radio-label">
      {I18n.t(`discovery.sellForm.shippingSection.domesticShippingCard.radios.label.${shippingModeTranslationKey(shippingMode)}`)}
      {shippingDefaultsEnabled && shippingMode === ShippingMode.CARRIER_CALCULATED &&
        <span className="ml-2">
          <RCTag variant="highlight">
            {I18n.t('discovery.sellForm.shippingSection.domesticShippingCard.highlights.recommended')}
          </RCTag>
        </span>
      }
    </span>
  );
}
